/**
 * Resolving a *semantic* attribute (the Client ID attribute, the Client
 * attribute…) to the key the current environment actually uses.
 *
 * A presentational attribute `key` is the backend attribute's `name` verbatim
 * (`Attribute/mappers.ts` — `key: attr.name`), and that name is data, not a
 * contract: it differs between environments and deployments (`client_id` in one,
 * `clientId` in another). So any UI that hardcodes a key is making a guess, and
 * when the guess misses there is NO error — the code just silently operates on a
 * key that matches no attribute. That is how "Client ID" stopped being selected
 * by default on a new space: `buildDraft` wrote `globals.clientId = { on: true }`
 * into a catalog whose actual key was `client_id`, creating a phantom entry while
 * the real attribute stayed off.
 *
 * Resolve through these helpers instead of comparing to a literal.
 */

/**
 * The "Client ID" system attribute — the client's code, used for access control.
 *
 * Does NOT include `client-id` (hyphen): in the DVB seed catalog that slug is a
 * DIFFERENT attribute labelled "Client" (the client reference), not "Client ID".
 * Treating them as aliases would silently bind the two together.
 *
 * Does intentionally share `client_id` (underscore) with {@link CLIENT_ALIASES}:
 * most backends carry only the Client reference under that slug, so it needs to
 * resolve as `"client"` in the common case. A backend that also carries a distinct
 * Client ID code should key it `clientId` (camelCase) or `clientID`.
 */
export const CLIENT_ID_ALIASES = ["clientId", "client_id", "clientID"] as const;

/**
 * The "Client" reference attribute — the client's name/reference, not its code.
 *
 * `client_id` (underscore) is intentionally included here and also appears in
 * {@link CLIENT_ID_ALIASES} — the two lists deliberately overlap on that slug
 * because most backends use it for the Client reference. They do NOT overlap on
 * `client-id` (hyphen), which belongs exclusively to the Client reference.
 */
export const CLIENT_ALIASES = [
  "client",
  "client-id",
  "client_id_name",
  "clientName",
  "client_id",
] as const;

/** The sharing/visibility attribute, rendered as its own column rather than a field. */
export const STATUS_ALIASES = ["status", "document_status", "documentStatus"] as const;

export const ENTITY_ALIASES = ["entity", "entity_id", "entityId"] as const;

export const INVESTOR_ALIASES = [
  "investor",
  "lp_id",
  "lp_investor",
  "lpInvestor",
  "lpId",
] as const;

export const PERIOD_ALIASES = ["period", "reporting_period", "reportingPeriod"] as const;

export const DOC_DATE_ALIASES = [
  "docDate",
  "document_date",
  "documentDate",
  "doc_date",
] as const;

/**
 * The vault-wide meanings the UI needs to reason about, independent of what any
 * one environment calls them.
 */
export type SemanticAttribute =
  "client" | "clientId" | "entity" | "investor" | "period" | "docDate" | "status";

const ALIASES_BY_SEMANTIC: Record<SemanticAttribute, readonly string[]> = {
  client: CLIENT_ALIASES,
  clientId: CLIENT_ID_ALIASES,
  entity: ENTITY_ALIASES,
  investor: INVESTOR_ALIASES,
  period: PERIOD_ALIASES,
  docDate: DOC_DATE_ALIASES,
  status: STATUS_ALIASES,
};

/**
 * Which meaning an environment-specific key carries, or undefined for an
 * attribute with no special handling (a steward- or space-authored one).
 *
 * Use this instead of `switch (key) { case "entity": … }` — that pattern silently
 * falls through to the default branch when the environment names the attribute
 * `entity_id`, which is how reference dropdowns ended up with no options.
 */
export function semanticOf(key: string): SemanticAttribute | undefined {
  const found = Object.entries(ALIASES_BY_SEMANTIC).find(([, aliases]) =>
    aliases.includes(key),
  );
  return found?.[0] as SemanticAttribute | undefined;
}

/**
 * First alias that exists in the catalog, else the first alias unchanged.
 *
 * The fallback keeps behaviour stable where the catalog genuinely lacks the
 * attribute (fixtures, stories, a space that never referenced it) — the caller
 * gets a deterministic key rather than `undefined`.
 */
export function resolveAttributeKey(
  aliases: readonly string[],
  catalog: readonly { key: string }[],
): string {
  return aliases.find((alias) => catalog.some((a) => a.key === alias)) ?? aliases[0];
}

/** The Client ID attribute key for this catalog. */
export const clientIdKeyFor = (catalog: readonly { key: string }[]): string =>
  resolveAttributeKey(CLIENT_ID_ALIASES, catalog);

export type ReferenceHierarchyKeys = {
  client: string | null;
  entity: string | null;
  investor: string | null;
};

/**
 * Resolve the reference hierarchy without returning fallback keys for roles the
 * current catalog does not contain.
 */
export function referenceHierarchyKeys(
  catalog: readonly { key: string }[],
): ReferenceHierarchyKeys {
  const existingKey = (aliases: readonly string[]) => {
    const resolved = resolveAttributeKey(aliases, catalog);
    return catalog.some((attribute) => attribute.key === resolved) ? resolved : null;
  };

  return {
    client: existingKey(CLIENT_ALIASES),
    entity: existingKey(ENTITY_ALIASES),
    investor: existingKey(INVESTOR_ALIASES),
  };
}

/** Ancestors that must exist before a hierarchy attribute can be referenced. */
export function referenceAncestorKeys(
  key: string,
  catalog: readonly { key: string }[],
): string[] {
  const hierarchy = referenceHierarchyKeys(catalog);
  if (key === hierarchy.investor)
    return [hierarchy.client, hierarchy.entity].filter(
      (ancestor): ancestor is string => ancestor !== null,
    );
  if (key === hierarchy.entity) return hierarchy.client ? [hierarchy.client] : [];
  return [];
}

/** Descendants invalidated when a hierarchy attribute is removed. */
export function referenceDescendantKeys(
  key: string,
  catalog: readonly { key: string }[],
): string[] {
  const hierarchy = referenceHierarchyKeys(catalog);
  if (key === hierarchy.client)
    return [hierarchy.entity, hierarchy.investor].filter(
      (descendant): descendant is string => descendant !== null,
    );
  if (key === hierarchy.entity) return hierarchy.investor ? [hierarchy.investor] : [];
  return [];
}

/**
 * Sort items so the three primary reference roles always lead in display order:
 * Client (index 0) → Entity (index 1) → Investor/LP (index 2). Every other item
 * stays in its original relative position after those three (stable sort).
 *
 * This is a display convention: the first three global fields are navigation
 * anchors and must appear in the same positions regardless of the order the
 * backend catalog returns them.
 */
export function sortGlobalsByRolePriority<T extends { key: string }>(
  items: T[],
  globalLib: readonly { key: string }[],
): T[] {
  const priority = [
    resolveAttributeKey(CLIENT_ALIASES, globalLib),
    resolveAttributeKey(ENTITY_ALIASES, globalLib),
    resolveAttributeKey(INVESTOR_ALIASES, globalLib),
  ];
  return items.toSorted((a, b) => {
    const ai = priority.indexOf(a.key);
    const bi = priority.indexOf(b.key);
    // Use priority.length (3) as the finite sentinel for non-priority keys so
    // the subtraction never produces NaN (Infinity - Infinity = NaN).
    return (ai === -1 ? priority.length : ai) - (bi === -1 ? priority.length : bi);
  });
}
